/**
 * AIVA-6639 — persistence for the AI disclaimer acknowledgement.
 *
 * Why localStorage, not sessionStorage (cf. `shared/sessionPref.ts`, AIVA-6685):
 * the acknowledgement must outlive the pane for the notice validity period, and a
 * task pane's sessionStorage is discarded every time it closes. To stay within
 * V-08 (no sensitive data in localStorage) the record carries no PII: the user
 * is reduced to a one-way hash in the key, and the value holds only the notice
 * version, host and two timestamps.
 *
 * This is a UX record, not evidence — the user can clear or edit it. Every
 * failure path is reported through {@link reportAiNoticeFailure} and degrades
 * to "not acknowledged", so a storage fault can only ever show the gate, never
 * skip it.
 */
import type { Channel } from "../shared/host";
import { DEFAULT_AI_NOTICE_POLICY, fnv1a, type AiNoticePolicy } from "./policy";
import { AI_NOTICE_REASON, reportAiNoticeFailure } from "./telemetry";

export type AiNoticeHost = Channel | "unknown";

export interface AiNoticeAcknowledgement {
  readonly noticeVersion: string;
  readonly host: AiNoticeHost;
  readonly acknowledgedAt: number;
  readonly expiresAt: number;
}

const KEY_PREFIX = "aiva.aiNotice.ack.";
/** Used when neither the token nor Studio yields an identity, so the gate still holds. */
const ANONYMOUS_IDENTITY = "anonymous";
const KNOWN_HOSTS: ReadonlySet<string> = new Set<AiNoticeHost>(["outlook", "word", "powerpoint", "unknown"]);

/** FNV-1a. Not cryptographic — it only keeps the raw email out of storage. */
function hashIdentity(value: string): string {
  return fnv1a(value);
}

function normaliseIdentity(identity: string | null | undefined): string {
  const trimmed = identity?.trim().toLowerCase() ?? "";
  return trimmed || ANONYMOUS_IDENTITY;
}

/**
 * Office partitions add-in storage in some hosts (e.g. Office on the web);
 * Microsoft's guidance is to prefix keys with `partitionKey` when present.
 */
function partitionPrefix(): string {
  // Outside an Office host (unit tests, a plain browser tab) there is no partition.
  if (typeof Office === "undefined") return "";
  try {
    const key: unknown = Office.context?.partitionKey;
    return typeof key === "string" ? key : "";
  } catch (err) {
    reportAiNoticeFailure(AI_NOTICE_REASON.partitionKeyFailed, err);
    return "";
  }
}

/**
 * Resolves localStorage, or null when it is absent or its getter throws
 * (blocked third-party storage raises a SecurityError on access).
 */
function resolveStorage(): Storage | null {
  try {
    const storage = globalThis.localStorage;
    if (storage) return storage;
    reportAiNoticeFailure(AI_NOTICE_REASON.storageUnavailable, new Error("localStorage is not available"));
  } catch (err) {
    reportAiNoticeFailure(AI_NOTICE_REASON.storageUnavailable, err);
  }
  return null;
}

export function acknowledgementKey(
  identity: string | null | undefined,
  policy: AiNoticePolicy = DEFAULT_AI_NOTICE_POLICY,
): string {
  return `${partitionPrefix()}${KEY_PREFIX}${hashIdentity(normaliseIdentity(identity))}.v${policy.version}`;
}

function isAcknowledgement(value: unknown, version: string): value is AiNoticeAcknowledgement {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v.noticeVersion === version &&
    typeof v.host === "string" &&
    KNOWN_HOSTS.has(v.host) &&
    typeof v.acknowledgedAt === "number" &&
    Number.isFinite(v.acknowledgedAt) &&
    typeof v.expiresAt === "number" &&
    Number.isFinite(v.expiresAt)
  );
}

/** Parses a stored value, reporting (and returning null for) anything malformed. */
function parseStoredRecord(raw: string, version: string): AiNoticeAcknowledgement | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    reportAiNoticeFailure(AI_NOTICE_REASON.recordInvalid, err);
    return null;
  }
  if (!isAcknowledgement(parsed, version)) {
    reportAiNoticeFailure(AI_NOTICE_REASON.recordInvalid, new Error("AI notice record has an unexpected shape"));
    return null;
  }
  return parsed;
}

/**
 * Valid from `acknowledgedAt` until the earlier of the stored expiry and
 * acknowledgedAt + the *current* TTL — so shortening the validity in Studio takes effect for
 * existing records, and a hand-edited far-future `expiresAt` is not honoured.
 * A record stamped in the future (clock moved back) is not trusted either.
 */
function isInValidityWindow(record: AiNoticeAcknowledgement, now: number, ttlMs: number): boolean {
  const effectiveExpiry = Math.min(record.expiresAt, record.acknowledgedAt + ttlMs);
  return record.acknowledgedAt <= now && now < effectiveExpiry;
}

function discardRecord(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch (err) {
    reportAiNoticeFailure(AI_NOTICE_REASON.cleanupFailed, err);
  }
}

/**
 * The stored acknowledgement for this identity at the current notice version,
 * or null when there is none, it has expired, it is malformed, or storage is
 * unavailable. Expired and malformed records are removed so they are not
 * re-parsed on every load.
 */
export function readAcknowledgement(
  identity: string | null | undefined,
  now: number = Date.now(),
  policy: AiNoticePolicy = DEFAULT_AI_NOTICE_POLICY,
): AiNoticeAcknowledgement | null {
  const storage = resolveStorage();
  if (!storage) return null;

  const key = acknowledgementKey(identity, policy);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch (err) {
    reportAiNoticeFailure(AI_NOTICE_REASON.readFailed, err);
    return null;
  }
  if (raw === null) return null;

  const record = parseStoredRecord(raw, policy.version);
  if (record && isInValidityWindow(record, now, policy.ttlMs)) return record;

  discardRecord(storage, key);
  return null;
}

/**
 * Persists an acknowledgement and returns it, or returns null (after reporting)
 * when storage is unavailable or the write is rejected — e.g. quota exceeded.
 * The caller must keep the gate up on null rather than let the user through
 * unrecorded.
 */
export function writeAcknowledgement(
  identity: string | null | undefined,
  host: Channel | null,
  now: number = Date.now(),
  policy: AiNoticePolicy = DEFAULT_AI_NOTICE_POLICY,
): AiNoticeAcknowledgement | null {
  const storage = resolveStorage();
  if (!storage) return null;

  const record: AiNoticeAcknowledgement = {
    noticeVersion: policy.version,
    host: host ?? "unknown",
    acknowledgedAt: now,
    expiresAt: now + policy.ttlMs,
  };
  try {
    storage.setItem(acknowledgementKey(identity, policy), JSON.stringify(record));
  } catch (err) {
    reportAiNoticeFailure(AI_NOTICE_REASON.writeFailed, err);
    return null;
  }
  return record;
}

/** Removes acknowledgements recorded against superseded notice versions. */
export function removeSupersededAcknowledgements(policy: AiNoticePolicy = DEFAULT_AI_NOTICE_POLICY): void {
  const storage = resolveStorage();
  if (!storage) return;

  const currentSuffix = `.v${policy.version}`;
  const stale: string[] = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.includes(KEY_PREFIX) && !key.endsWith(currentSuffix)) stale.push(key);
    }
  } catch (err) {
    reportAiNoticeFailure(AI_NOTICE_REASON.cleanupFailed, err);
    return;
  }
  // Collected first: removing while iterating shifts the indices.
  stale.forEach((key) => discardRecord(storage, key));
}
